import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CreditCard, Palette, CheckCircle, ArrowRight, ArrowLeft, Sparkles, Users, MapPin, HardDrive, Check } from 'lucide-react';
import { usePlatformStore } from '@/lib/store';
import { Spinner } from '@/components/shared/UIComponents';
import { slugify, formatCurrency } from '@/utils/helpers';
import { FEATURE_META } from '@/lib/constants';
import { fetchPlanFeatureDefaults } from '@/lib/api';
import type { TenantPlan, CreateTenantInput } from '@/types';

const STEPS = [
  { id: 'org',      label: 'Organisation', icon: Building2 },
  { id: 'plan',     label: 'Select Plan',  icon: CreditCard },
  { id: 'branding', label: 'Branding',     icon: Palette },
  { id: 'review',   label: 'Review',       icon: CheckCircle },
];

const PRESET_COLORS = [
  // Vibrant
  '#0d9488', '#ef4444', '#f97316', '#f59e0b', '#84cc16',
  '#10b981', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#ec4899', '#f43f5e', '#64748b',
  // Corporate
  '#1e3a5f', '#2c5282', '#1a365d', '#234e52', '#22543d',
  '#742a2a', '#7b341e', '#44337a', '#1a202c', '#2d3748',
  // Luxury
  '#b8860b', '#c9a84c', '#5b2c6f', '#1b4332', '#4a1942',
  '#8b6914', '#2c3e50', '#6b4226', '#3d0c11', '#1c1c1c',
];

export function ProvisionWizardPage() {
  const navigate = useNavigate();
  const { plans, createTenant, loading, loadPlans } = usePlatformStore();
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    slug: '',
    owner_email: '',
    plan_id: '',
    primary_color: '#0d9488',
    secondary_color: '',
    app_name: '',
    welcome_message: '',
    font_family: '',
  });
  const [autoSlug, setAutoSlug] = useState(true);

  // Ensure plans are loaded
  useEffect(() => { loadPlans(); }, [loadPlans]);

  const set = (field: string, value: string) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'name' && autoSlug) next.slug = slugify(value);
      if (field === 'name' && !prev.app_name) next.app_name = value;
      return next;
    });
  };

  const selectedPlan = plans.find(p => p.id === form.plan_id) as TenantPlan | undefined;

  // ── Validation per step ──────────────────────────────────────────────────

  const validateStep = (): boolean => {
    setError('');
    if (step === 0) {
      if (!form.name.trim()) { setError('Organisation name is required'); return false; }
      if (!form.slug.trim()) { setError('URL slug is required'); return false; }
      if (form.slug.length < 3) { setError('Slug must be at least 3 characters'); return false; }
    }
    if (step === 1) {
      if (!form.plan_id) { setError('Please select a plan'); return false; }
    }
    return true;
  };

  const goNext = () => { if (validateStep()) setStep(s => Math.min(s + 1, STEPS.length - 1)); };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  // ── Submit ───────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    setError('');
    try {
      const tenant = await createTenant({
        name: form.name,
        slug: form.slug,
        plan_id: form.plan_id,
        owner_email: form.owner_email,
        branding: {
          primary_color: form.primary_color,
          secondary_color: form.secondary_color || undefined,
          app_name: form.app_name || form.name,
          welcome_message: form.welcome_message || undefined,
          font_family: form.font_family || undefined,
        },
      } as CreateTenantInput);

      // Owner invite is now handled inside createTenant (api.ts) as fire-and-forget

      // Pass owner_email through navigation state so ProvisionSuccess can
      // offer a Resend Invite button. Without this, if the first invite
      // email fails (Resend rate limit, bad address, transport error),
      // the operator has no way to know — and the tester is locked out of
      // their tenant admin. The success page reads location.state.ownerEmail.
      navigate(`/admin/tenants/${tenant.id}/provision-success`, {
        state: { ownerEmail: form.owner_email },
      });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-900">Provision New Tenant</h1>
        <p className="text-surface-500 mt-1">Set up a new organisation on the DiscoveryGem platform</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === step;
          const isDone = i < step;
          return (
            <div key={s.id} className="flex items-center gap-2 flex-1">
              <button
                onClick={() => i < step && setStep(i)}
                disabled={i > step}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all w-full
                  ${isActive ? 'badge-brand border border-brand-500/20' :
                    isDone ? 'badge-emerald border border-emerald-500/20 cursor-pointer' :
                    'bg-surface-50 text-surface-500 border border-surface-300'}`}
              >
                {isDone ? <Check size={16} /> : <Icon size={16} />}
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
              {i < STEPS.length - 1 && (
                <div className={`w-4 h-px shrink-0 ${isDone ? 'bg-emerald-500' : 'bg-surface-200'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-400">{error}</div>
      )}

      {/* Step content */}
      <div className="glass-panel p-6">
        {step === 0 && <StepOrg form={form} set={set} autoSlug={autoSlug} setAutoSlug={setAutoSlug} />}
        {step === 1 && <StepPlan form={form} set={set} plans={plans} />}
        {step === 2 && <StepBranding form={form} set={set} />}
        {step === 3 && <StepReview form={form} selectedPlan={selectedPlan} />}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between">
        <button onClick={goBack} disabled={step === 0} className="btn-secondary disabled:opacity-30">
          <ArrowLeft size={16} /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button onClick={goNext} className="btn-primary">
            Next <ArrowRight size={16} />
          </button>
        ) : (
          <button onClick={handleCreate} disabled={loading} className="btn-primary">
            {loading ? <Spinner size="sm" /> : <Sparkles size={16} />}
            Create Tenant
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* STEP 1: Organisation Details                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

function StepOrg({ form, set, autoSlug, setAutoSlug }: {
  form: any; set: (f: string, v: string) => void;
  autoSlug: boolean; setAutoSlug: (v: boolean) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-surface-900">Organisation Details</h2>
        <p className="text-sm text-surface-500 mt-1">Basic information about the tenant organisation</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="input-label">Organisation Name *</label>
          <input className="input-field" placeholder="Wanderlust Travel Agency" value={form.name}
            onChange={e => set('name', e.target.value)} />
        </div>
        <div>
          <label className="input-label">URL Slug *</label>
          <div className="relative">
            <input className="input-field pr-28" placeholder="wanderlust" value={form.slug}
              onChange={e => { setAutoSlug(false); set('slug', e.target.value); }} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-surface-500">.discoverygem.com</span>
          </div>
          <p className="text-xs text-surface-500 mt-1">This will be their subdomain: <span className="text-surface-700 font-mono">{form.slug || '___'}.discoverygem.com</span></p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="input-label">Owner Email</label>
          <input className="input-field" type="email" placeholder="admin@wanderlust.com"
            value={form.owner_email} onChange={e => set('owner_email', e.target.value)} />
          <p className="text-xs text-surface-500 mt-1">The primary admin who'll manage this tenant.</p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* STEP 2: Plan Selection                                                     */
/* ═══════════════════════════════════════════════════════════════════════════ */

function StepPlan({ form, set, plans }: {
  form: any; set: (f: string, v: string) => void; plans: TenantPlan[];
}) {
  const [planFeatures, setPlanFeatures] = useState<Record<string, boolean>>({});
  const [featureCounts, setFeatureCounts] = useState<Record<string, number>>({});

  // Fetch feature counts for all plans on mount
  useEffect(() => {
    plans.forEach(plan => {
      fetchPlanFeatureDefaults(plan.id).then(rows => {
        const count = rows.filter(r => r.is_enabled).length;
        setFeatureCounts(prev => ({ ...prev, [plan.id]: count }));
      });
    });
  }, [plans]);

  // Fetch features when selected plan changes
  useEffect(() => {
    if (form.plan_id) {
      fetchPlanFeatureDefaults(form.plan_id).then(rows => {
        const map: Record<string, boolean> = {};
        rows.forEach(r => map[r.feature_key] = r.is_enabled);
        setPlanFeatures(map);
      });
    }
  }, [form.plan_id]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-surface-900">Select a Plan</h2>
        <p className="text-sm text-surface-500 mt-1">Choose the plan tier for this tenant. They can upgrade later.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {plans.map(plan => {
          const selected = form.plan_id === plan.id;
          const enabledCount = featureCounts[plan.id] ?? 0;

          return (
            <button key={plan.id} onClick={() => set('plan_id', plan.id)}
              className={`p-5 rounded-2xl border text-left transition-all ${
                selected
                  ? 'border-brand-500 bg-brand-500/5 shadow-[0_0_0_1px_rgba(229,77,46,0.3)]'
                  : 'border-surface-300 bg-surface-50 hover:border-surface-400 hover:bg-surface-100'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-base font-semibold text-surface-900">{plan.display_name}</div>
                  <div className="text-xs text-surface-500 mt-0.5">{plan.description}</div>
                </div>
                <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5"
                  style={{ borderColor: selected ? '#0d9488' : 'rgba(255,255,255,0.15)' }}>
                  {selected && <div className="w-2.5 h-2.5 rounded-full bg-brand-500" />}
                </div>
              </div>

              <div className="mb-3">
                {plan.price_per_seat_monthly === 0 ? (
                  <div className="text-2xl font-bold text-surface-900">{plan.name === 'enterprise' ? 'Custom' : 'Free'}</div>
                ) : (
                  <div>
                    <span className="text-2xl font-bold text-surface-900">{formatCurrency(plan.price_per_seat_monthly)}</span>
                    <span className="text-sm text-surface-500"> /seat/mo</span>
                  </div>
                )}
              </div>

              <div className="space-y-1.5 text-xs">
                <PlanRow icon={<Users size={12} />} text={plan.max_users ? `Up to ${plan.max_users} users` : 'Unlimited users'} />
                <PlanRow icon={<MapPin size={12} />} text={plan.max_trips_per_month ? `${plan.max_trips_per_month} trips/mo` : 'Unlimited trips'} />
                <PlanRow icon={<HardDrive size={12} />} text={`${plan.max_storage_mb >= 1024 ? `${plan.max_storage_mb / 1024} GB` : `${plan.max_storage_mb} MB`} storage`} />
                <PlanRow icon={<Sparkles size={12} />} text={`${enabledCount} features included`} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Feature comparison hint */}
      {form.plan_id && (
        <div className="p-4 rounded-xl bg-surface-50 border border-surface-300">
          <div className="text-xs text-surface-500 mb-2">Features included in <span className="text-surface-900 capitalize">{plans.find(p => p.id === form.plan_id)?.name}</span>:</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(planFeatures)
              .filter(([_, v]) => v)
              .sort(([a], [b]) => (FEATURE_META[a]?.label || '').localeCompare(FEATURE_META[b]?.label || ''))
              .map(([key]) => (
                <span key={key} className="px-2 py-0.5 rounded-md badge-emerald text-[10px] font-medium border border-emerald-500/10">
                  {FEATURE_META[key]?.label || key}
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PlanRow({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 text-surface-500">
      <span className="text-surface-500">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* STEP 3: Branding                                                           */
/* ═══════════════════════════════════════════════════════════════════════════ */

function StepBranding({ form, set }: { form: any; set: (f: string, v: string) => void }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-surface-900">Initial Branding</h2>
        <p className="text-sm text-surface-500 mt-1">Set up basic branding. This can be fully customised later in the Tenant Admin.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className="input-label">App Name</label>
            <input className="input-field" value={form.app_name} onChange={e => set('app_name', e.target.value)}
              placeholder={form.name || 'My Travel App'} />
            <p className="text-xs text-surface-500 mt-1">This replaces "DiscoveryGem" throughout the app</p>
          </div>

          <div>
            <label className="input-label">Brand Colour</label>
            <div className="flex items-center gap-3 mb-2">
              <input type="color" value={form.primary_color} onChange={e => set('primary_color', e.target.value)}
                className="w-10 h-10 rounded-lg border-2 border-white/10 cursor-pointer bg-transparent shrink-0" />
              <input className="input-field w-28 font-mono text-sm" value={form.primary_color}
                onChange={e => set('primary_color', e.target.value)} />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {PRESET_COLORS.map(c => (
                <button key={c} onClick={() => set('primary_color', c)}
                  className={`w-7 h-7 rounded-lg border-2 transition-transform hover:scale-110 ${
                    form.primary_color === c ? 'border-white scale-110' : 'border-white/10'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>

          <div>
            <label className="input-label">Welcome Message</label>
            <input className="input-field" value={form.welcome_message} onChange={e => set('welcome_message', e.target.value)}
              placeholder="Explore the world with us" />
          </div>

          <div>
            <label className="input-label">Font (optional)</label>
            <select className="input-field" value={form.font_family} onChange={e => set('font_family', e.target.value)}>
              <option value="">Default (DM Sans)</option>
              {['Outfit', 'Plus Jakarta Sans', 'Poppins', 'Nunito', 'Montserrat', 'Sora', 'Manrope'].map(f =>
                <option key={f} value={f}>{f}</option>
              )}
            </select>
          </div>
        </div>

        {/* Mini preview */}
        <div>
          <label className="input-label mb-2">Preview</label>
          <div className="rounded-2xl overflow-hidden border border-surface-300">
            <div className="h-10 flex items-center px-3 gap-2" style={{ backgroundColor: form.primary_color }}>
              <div className="w-5 h-5 rounded bg-white/20" />
              <span className="text-xs font-semibold text-surface-900">{form.app_name || form.name || 'App'}</span>
            </div>
            <div className="bg-surface-100 p-4 space-y-3" style={{ fontFamily: form.font_family || 'DM Sans' }}>
              <div className="text-xs text-surface-500">{form.welcome_message || 'Welcome'}</div>
              <div className="flex gap-2">
                <div className="h-7 rounded-lg px-4 flex items-center text-[11px] text-surface-900 font-medium"
                  style={{ backgroundColor: form.primary_color }}>New Trip</div>
                <div className="h-7 rounded-lg px-4 flex items-center text-[11px] text-surface-700 bg-surface-100">Browse</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* STEP 4: Review                                                             */
/* ═══════════════════════════════════════════════════════════════════════════ */

function StepReview({ form, selectedPlan }: { form: any; selectedPlan?: TenantPlan }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-surface-900">Review & Create</h2>
        <p className="text-sm text-surface-500 mt-1">Confirm everything looks correct before provisioning</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Organisation */}
        <ReviewSection title="Organisation">
          <ReviewRow label="Name" value={form.name} />
          <ReviewRow label="Slug" value={form.slug} />
          <ReviewRow label="Subdomain" value={`${form.slug}.discoverygem.com`} highlight />
          <ReviewRow label="Owner Email" value={form.owner_email || 'Not set'} />
        </ReviewSection>

        {/* Plan */}
        <ReviewSection title="Plan">
          <ReviewRow label="Plan" value={selectedPlan?.display_name || '—'} />
          <ReviewRow label="Price" value={selectedPlan?.price_per_seat_monthly === 0 ? 'Free' : `${formatCurrency(selectedPlan?.price_per_seat_monthly || 0)}/seat/mo`} />
          <ReviewRow label="Max Users" value={selectedPlan?.max_users ? String(selectedPlan.max_users) : 'Unlimited'} />
        </ReviewSection>

        {/* Branding */}
        <ReviewSection title="Branding">
          <ReviewRow label="App Name" value={form.app_name || form.name} />
          <div className="flex items-center gap-2 py-1.5">
            <span className="text-xs text-surface-500 w-24 shrink-0">Brand Colour</span>
            <div className="w-5 h-5 rounded border border-white/10" style={{ backgroundColor: form.primary_color }} />
            <span className="text-xs text-surface-700 font-mono">{form.primary_color}</span>
          </div>
          <ReviewRow label="Welcome" value={form.welcome_message || 'Default'} />
          <ReviewRow label="Font" value={form.font_family || 'Default (DM Sans)'} />
        </ReviewSection>

        {/* What happens next */}
        <div className="glass-panel p-4 bg-brand-500/5 border-brand-500/10">
          <h4 className="text-sm font-semibold text-surface-900 mb-2">What happens next</h4>
          <div className="space-y-2 text-xs text-surface-500">
            <div className="flex gap-2"><Check size={12} className="text-emerald-400 shrink-0 mt-0.5" /> Tenant record is created and activated</div>
            <div className="flex gap-2"><Check size={12} className="text-emerald-400 shrink-0 mt-0.5" /> Branding and feature configs are initialized from the plan</div>
            <div className="flex gap-2"><Check size={12} className="text-emerald-400 shrink-0 mt-0.5" /> Subdomain <span className="text-surface-900 font-mono">{form.slug}.discoverygem.com</span> becomes active</div>
            <div className="flex gap-2"><Check size={12} className="text-emerald-400 shrink-0 mt-0.5" /> Owner receives an invite to the Tenant Admin dashboard</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="glass-panel p-4">
      <h4 className="text-sm font-semibold text-surface-900 mb-3">{title}</h4>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-center gap-2 py-1.5">
      <span className="text-xs text-surface-500 w-24 shrink-0">{label}</span>
      <span className={`text-xs ${highlight ? 'text-brand-600 dark:text-brand-400 font-mono' : 'text-surface-700'}`}>{value}</span>
    </div>
  );
}
